import {
  DAY_MS,
  DEFAULT_WEEK_START_DAY,
  WEEK_MS,
  calendarWeeksAgo,
  computeWeekStreak,
  isCardioSession,
  type Session,
  type SessionStart,
  type WeekStreak,
} from "@heraion/shared";

// Session-count summaries behind the Progress footer (lifted from mobile's
// progressScreenMath). The frequency bar chart + the total/7d/30d/last
// stats read these.

export const FREQ_WEEKS = 12;

export type SessionSummary = {
  total: number;
  last7d: number;
  last30d: number;
  lastSession: Pick<Session, "startedAt"> | null;
};

// #238: reads only `startedAt`, so the host can keep the starts, not the rows.
export function computeSessionSummary(
  sessions: ReadonlyArray<Pick<Session, "startedAt">>,
  nowMs: number,
): SessionSummary {
  let last7d = 0;
  let last30d = 0;
  let lastSession: Pick<Session, "startedAt"> | null = null;
  let lastT = 0;
  for (const s of sessions) {
    const at = new Date(s.startedAt).getTime();
    if (nowMs - at <= WEEK_MS) last7d++;
    if (nowMs - at <= 30 * DAY_MS) last30d++;
    if (at > lastT) {
      lastT = at;
      lastSession = s;
    }
  }
  return { total: sessions.length, last7d, last30d, lastSession };
}

// Widened to the one field it reads (CO-9): the coach detail buckets the
// athlete's session-results (which carry only a timestamp), while the mobile
// host keeps passing full Session rows — structurally compatible, no change.
// PG-18: calendar weeks from the athlete's weekStartDay, the last bar being
// the current (partial) week.
export function computeWeeklyCounts(
  sessions: ReadonlyArray<Pick<Session, "startedAt">>,
  nowMs: number,
  weeks: number = FREQ_WEEKS,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): number[] {
  const out = new Array<number>(weeks).fill(0);
  for (const s of sessions) {
    const ago = calendarWeeksAgo(
      new Date(s.startedAt).getTime(),
      nowMs,
      weekStartDay,
    );
    // Also drops an unparseable date (NaN fails both sides).
    if (!(ago >= 0 && ago < weeks)) continue;
    const weekIdx = weeks - 1 - ago;
    out[weekIdx] = (out[weekIdx] ?? 0) + 1;
  }
  return out;
}

/** #321: which sessions the Rhythm counts, the athlete's choice. */
export type RhythmKind = "strength" | "cardio" | "all";
export const RHYTHM_KINDS: readonly RhythmKind[] = ["strength", "cardio", "all"];
export const parseRhythmKind = (raw: string): RhythmKind | null =>
  RHYTHM_KINDS.find((k) => k === raw) ?? null;
/** The choice's storage base; each host scopes it to the account. */
export const RHYTHM_KIND_KEY = "jl.progressRhythmKind.v1";

/** The Rhythm's numbers for one kind of session. */
export type RhythmRecap = { summary: SessionSummary; weeklyCounts: number[] };

/** Everything Progress reads off the sessions list; empty while it loads. */
export type SessionsRecap = {
  rhythm: Record<RhythmKind, RhythmRecap> | null;
  weekStreak: WeekStreak | null;
};

// One step from the sessions to the stats, the weekly counts (PG-18) and the
// streak (PG-58): mobile's Progress and useSessionsSummary both call it.
// #321: the Rhythm's numbers once per kind; the streak counts every session.
export function summarizeSessions(
  sessions: ReadonlyArray<SessionStart> | null,
  nowMs: number,
  weekStartDay: number | undefined,
): SessionsRecap {
  if (sessions === null) return { rhythm: null, weekStreak: null };
  const recap = (rows: ReadonlyArray<SessionStart>): RhythmRecap => ({
    summary: computeSessionSummary(rows, nowMs),
    weeklyCounts: computeWeeklyCounts(rows, nowMs, undefined, weekStartDay),
  });
  return {
    rhythm: {
      strength: recap(sessions.filter((s) => !isCardioSession(s))),
      cardio: recap(sessions.filter(isCardioSession)),
      all: recap(sessions),
    },
    weekStreak: computeWeekStreak(sessions, nowMs, weekStartDay),
  };
}
